import React, { FC, useCallback, useState } from "react";

import { EmailTemplateRecipient } from "../../settings/templates/emailTemplates/emailTemplates.utils";
import { ComposeEmailModal } from "../modals/ComposeEmailModal";
import { clientRecipient, ClientRecipientInput } from "./clientEmail.utils";

/**
 * Client email addresses open ProCharted's own compose window (owner
 * 2026-10-22, CONT-009) instead of a mailto: link. The To box gets the client
 * as a "Client" chip and the window knows the client (customerId) for the AI
 * and the client's Email tab. One window per page: call `openCompose` from a
 * row/link and render `composeModal` once.
 */
export const useClientEmailCompose = () => {
  const [to, setTo] = useState<EmailTemplateRecipient | null>(null);

  const openCompose = useCallback((input: ClientRecipientInput) => {
    const recipient = clientRecipient(input);
    if (recipient) setTo(recipient);
  }, []);

  const composeModal = to ? (
    <ComposeEmailModal
      isActive
      contactEmail={[to]}
      customerId={to.customerId}
      onClose={() => setTo(null)}
    />
  ) : null;

  return { openCompose, composeModal };
};

type LinkProps = {
  email?: string | null;
  className?: string;
  onOpen: () => void;
  children?: React.ReactNode;
};

/** Looks like a link, opens the compose window (never the computer's mail app). */
export const ClientEmailLink: FC<LinkProps> = ({
  email,
  className,
  onOpen,
  children,
}) => {
  if (!email?.trim()) return null;
  return (
    <button
      type={"button"}
      title={`Write an email to ${email}`}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}>
      {children ?? email}
    </button>
  );
};
